import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/app/Logo";

export default function LoginPage() {
  const { login, error, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [coverUrl, setCoverUrl] = useState(
    "https://customer-assets.emergentagent.com/job_pup-show-tracker/artifacts/x9ktdk8l_Tiina%20with%20Dino.JPG"
  );

  useEffect(() => {
    api.get("/settings/cover")
      .then(({ data }) => { if (data.cover_url) setCoverUrl(data.cover_url); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.id) {
      const dest = loc.state?.from?.pathname || "/welcome";
      nav(dest, { replace: true });
    }
  }, [user, nav, loc.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) nav("/welcome");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative overflow-hidden bg-primary">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt="Show poodle"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
        <div className="absolute inset-0 bg-primary/30" />
        <div className="absolute bottom-10 left-10 right-10 text-primary-foreground">
          <p className="text-xs uppercase tracking-[0.3em] mb-4 opacity-80">Your Dog Days</p>
          <h2 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight">
            Everything you and your dog need. Now go learn and experience life together.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md border-border shadow-none rounded-lg">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-8">
              <Logo size={64} textClassName="text-2xl" />
            </div>

            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Welcome back</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mb-8">
              Sign into Your Dog Days
            </h1>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    data-testid="goto-forgot-password-link"
                    className="text-sm font-medium text-primary underline underline-offset-4 hover:opacity-80"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              {error ? (
                <div data-testid="login-error" className="text-sm text-destructive">{error}</div>
              ) : null}
              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={submitting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98] transition-all"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>

            <p className="text-sm text-muted-foreground mt-6">
              New here?{" "}
              <Link to="/register" className="underline underline-offset-4 text-foreground" data-testid="goto-register-link">
                Create an account
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
